import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGraphQL } from "../../context/GraphQLProvider";
import DashboardMainContainer from "../../components/common/Containers/DashboardMainContainer";
import DashboardAdsCard from "../../components/common/AdsCard/DashboardAdsCard";
import Alerts from "../../components/common/Alerts/Alerts";
import {
  AdminPageTitle,
  AdminSupportingText,
  AdminHeaderAction,
} from "../../sections/AdminDashboard/shared/adminUx";
import {
  CUSTOMER_CENTERED_PAGE_TITLE_CLASS,
  CUSTOMER_POST_GRID_CLASS,
  CUSTOMER_LIST_FOOTER_CLASS,
} from "../../sections/CustomerDashboard/shared/customerLayoutSpec";

const RecentlyViewedPosts = () => {
  const { user: userId } = useSelector((state) => state.user);
  const graphqlService = useGraphQL();
  const [allViewedPosts, setAllViewedPosts] = useState([]);
  const [displayedPosts, setDisplayedPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [clearingHistoryPostId, setClearingHistoryPostId] = useState(null);
  const navigate = useNavigate();

  const fetchRecentlyViewedPosts = async () => {
    if (userId) {
      setIsLoading(true);
      try {
        // TODO: Replace with actual GraphQL query for recently viewed posts
        // const response = await GetRecentlyViewedPosts(graphqlService, {
        //   userId: parseFloat(userId.id)
        // });
        // const posts = response?.getRecentlyViewedPosts || [];
        
        // Dummy data for testing
        const posts = [
          {
            id: 11,
            postTitle: "Roofing Repairs & Installation",
            postDescription: "Professional roofing services for all types of properties. Quality guaranteed.",
            mainCategory: "Roofing",
            subCategory: "Repairs",
            content: JSON.stringify([
              { original: require("../../assets/img/P2.jpeg"), thumbnail: require("../../assets/img/P2.jpeg") }
            ]),
            userId: 11,
            postKeywords: ["roofing", "repairs", "installation"],
            postRadius: "20km",
            postSuburb: "Newcastle",
            isHighlighted: true,
            likes: 67,
            views: 540,
            viewedAt: "2024-01-15T10:30:00Z"
          },
          {
            id: 12,
            postTitle: "HVAC Services & Maintenance",
            postDescription: "Heating, ventilation, and air conditioning services for your comfort.",
            mainCategory: "HVAC",
            subCategory: "Maintenance",
            content: JSON.stringify([
              { original: require("../../assets/img/P3.jpeg"), thumbnail: require("../../assets/img/P3.jpeg") },
              { original: require("../../assets/img/P4.jpeg"), thumbnail: require("../../assets/img/P4.jpeg") }
            ]),
            userId: 12,
            postKeywords: ["hvac", "heating", "cooling"],
            postRadius: "15km",
            postSuburb: "Wollongong",
            isHighlighted: false,
            likes: 42,
            views: 298,
            viewedAt: "2024-01-15T09:15:00Z"
          },
          {
            id: 13,
            postTitle: "Tile & Floor Installation",
            postDescription: "Expert tiling services for bathrooms, kitchens, and outdoor areas.",
            mainCategory: "Tiling",
            subCategory: "Floor Installation",
            content: JSON.stringify([
              { original: require("../../assets/img/P5.jpeg"), thumbnail: require("../../assets/img/P5.jpeg") }
            ]),
            userId: 13,
            postKeywords: ["tiling", "flooring", "installation"],
            postRadius: "25km",
            postSuburb: "Central Coast",
            isHighlighted: false,
            likes: 35,
            views: 225,
            viewedAt: "2024-01-14T16:45:00Z"
          },
          {
            id: 14,
            postTitle: "Kitchen Renovation Specialists",
            postDescription: "Transform your kitchen with our expert renovation services.",
            mainCategory: "Renovation",
            subCategory: "Kitchen",
            content: JSON.stringify([
              { original: require("../../assets/img/P77.jpg"), thumbnail: require("../../assets/img/P77.jpg") },
              { original: require("../../assets/img/P1.jpeg"), thumbnail: require("../../assets/img/P1.jpeg") }
            ]),
            userId: 14,
            postKeywords: ["kitchen", "renovation", "remodeling"],
            postRadius: "30km",
            postSuburb: "Penrith",
            isHighlighted: true,
            likes: 89,
            views: 672,
            viewedAt: "2024-01-14T14:20:00Z"
          },
          {
            id: 15,
            postTitle: "Window & Door Installation",
            postDescription: "Professional installation of windows and doors for homes and businesses.",
            mainCategory: "Windows & Doors",
            subCategory: "Installation",
            content: JSON.stringify([
              { original: require("../../assets/img/P2.jpeg"), thumbnail: require("../../assets/img/P2.jpeg") }
            ]),
            userId: 15,
            postKeywords: ["windows", "doors", "installation"],
            postRadius: "20km",
            postSuburb: "Liverpool",
            isHighlighted: false,
            likes: 31,
            views: 198,
            viewedAt: "2024-01-14T11:30:00Z"
          },
          {
            id: 16,
            postTitle: "Bathroom Renovation Experts",
            postDescription: "Complete bathroom renovation services with modern designs.",
            mainCategory: "Renovation",
            subCategory: "Bathroom",
            content: JSON.stringify([
              { original: require("../../assets/img/P3.jpeg"), thumbnail: require("../../assets/img/P3.jpeg") }
            ]),
            userId: 16,
            postKeywords: ["bathroom", "renovation", "modern"],
            postRadius: "18km",
            postSuburb: "Hornsby",
            isHighlighted: false,
            likes: 44,
            views: 312,
            viewedAt: "2024-01-13T15:50:00Z"
          }
        ];
        
        setAllViewedPosts(posts);
        // Show first 9 posts by default
        setDisplayedPosts(posts.slice(0, 9));
      } catch (error) {
        console.error("Error fetching recently viewed posts:", error);
        Alerts.error("Failed to fetch recently viewed posts. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchRecentlyViewedPosts();
  }, []);

  const handleLoadMore = () => {
    setIsLoadingMore(true);
    // Simulate loading delay for better UX
    setTimeout(() => {
      const currentCount = displayedPosts.length;
      const nextBatch = allViewedPosts.slice(currentCount, currentCount + 6);
      setDisplayedPosts(prev => [...prev, ...nextBatch]);
      setIsLoadingMore(false);
    }, 500);
  };

  const handleViewPost = (postId) => {
    navigate(`/service-detail/?id=53`);
  };

  const handleRemoveFromHistory = async (postId) => {
    setClearingHistoryPostId(postId);
    try {
      // TODO: Replace with actual GraphQL mutation to remove from history
      // await RemoveFromViewHistoryMutation(graphqlService, {
      //   userId: parseFloat(userId.id),
      //   postId: postId
      // });
      
      // Remove from both arrays
      setAllViewedPosts(prev => prev.filter(post => post.id !== postId));
      setDisplayedPosts(prev => prev.filter(post => post.id !== postId));
      Alerts.success("Post removed from history.");
    } catch (error) {
      console.error("Error removing post from history:", error);
      Alerts.error("We couldn't remove this post. Please try again later.");
    } finally {
      setClearingHistoryPostId(null);
    }
  };

  const hasMorePosts = displayedPosts.length < allViewedPosts.length;

  if (isLoading) {
    return (
      <DashboardMainContainer className="pb-10 !bg-white">
        <AdminPageTitle className={CUSTOMER_CENTERED_PAGE_TITLE_CLASS}>
          Recently Viewed Posts
        </AdminPageTitle>
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primaryblue"></div>
        </div>
      </DashboardMainContainer>
    );
  }

  return (
    <DashboardMainContainer className="pb-10 !bg-white">
      <AdminPageTitle className={CUSTOMER_CENTERED_PAGE_TITLE_CLASS}>
        Recently Viewed Posts
      </AdminPageTitle>
      
      {displayedPosts?.length === 0 ? (
        <div className="text-center">
          <i className="fi fi-rs-time-past text-6xl text-mediumgray mb-4" aria-hidden />
          <AdminSupportingText className="text-offblack block">No recently viewed posts</AdminSupportingText>
          <AdminSupportingText className="text-mediumgray mt-2 block">
            Posts you view will appear here
          </AdminSupportingText>
        </div>
      ) : (
        <div>
          <div className={CUSTOMER_POST_GRID_CLASS}>
            {displayedPosts.map((post) => (
              <div 
                key={post.id} 
                className={`relative ${clearingHistoryPostId === post.id ? 'opacity-50 pointer-events-none' : ''}`}
              >
                {clearingHistoryPostId === post.id && (
                  <div className="absolute inset-0 flex items-center justify-center z-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primaryblue"></div>
                  </div>
                )}
                <DashboardAdsCard
                  saveButton={false}
                  additionalActions={true}
                  editButton={false}
                  editOnClick={() => handleViewPost(post.id)}
                  viewButton={true}
                  viewOnClick={() => handleViewPost(post.id)}
                  deleteButton={true}
                  deleteOnClick={() => handleRemoveFromHistory(post.id)}
                  postTitle={post.postTitle}
                  serviceDescription={post.postDescription}
                  category={post.mainCategory}
                  subCategory={post.subCategory}
                  Images={post.content ? JSON.parse(post.content) : []}
                  serviceId={post.id}
                  companyId={post.userId}
                  postKeywords={post.postKeywords}
                  postRadius={post.postRadius}
                  postSuburb={post.postSuburb}
                  isHighlighted={post.isHighlighted}
                  likes={post.likes}
                  views={post.views}
                  viewedAt={post.viewedAt}
                />
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {hasMorePosts && (
            <div className={CUSTOMER_LIST_FOOTER_CLASS}>
              <AdminHeaderAction onClick={handleLoadMore} disabled={isLoadingMore}>
                {isLoadingMore ? "Loading..." : "Load more"}
              </AdminHeaderAction>
            </div>
          )}
        </div>
      )}
    </DashboardMainContainer>
  );
};

export default RecentlyViewedPosts;

